import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DataStateChangeEvent,
  FilterService,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import {
  ColumnOptions,
  DateHelper,
  GridRowType,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  NamedEntity,
} from '@shared';
import { ChangesetService, RestaurantService } from '@shared-data';
import { TokenService } from '@auth';
import { GridRowAvailabilityChangeset, 
  GridRowChangeset, 
  GridRowChangesetModifierCodes, 
  GridRowRealignmentChangeset, 
  IScheduledChangeset } from '../../models';
import { CompositeFilterDescriptor, groupBy, GroupDescriptor, GroupResult } from '@progress/kendo-data-query';
import { forkJoin } from 'rxjs';
import { CHANGESET_STRATEGIES, ChangesetType } from '@restaurant-item';
import { environment } from 'src/environments/environment';

interface HistoryTypeSection {
  type: string;
  columns: ColumnOptions[];
  changesets: GridRowType[] | GroupResult[];
  state: GridState;
  filterCache: Map<string, string[]>;
  distinctParams?: Record<string, string | string[]>;
}

const CUSTOM_FILTER_FIELDS = [
  'parItemId',
  'restaurantName',
  'itemName',
  'componentName',
  'componentType',
  'modifierCode',
];

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
  encapsulation: ViewEncapsulation.None,
   providers: [
    {
      provide: CHANGESET_STRATEGIES,
      useValue: {
        pricing: new GridRowChangeset(),
        availability: new GridRowAvailabilityChangeset(), 
        realignment: new GridRowRealignmentChangeset(),
        modifiercodes: new GridRowChangesetModifierCodes(),
      },
    },
  ],
})
export class HistoryComponent {
  pageSize = 10;
  isAvailabilityOnly: boolean = false;

  types: string[] = [];
  scheduledDate?: string;
  tierId?: string;
  transmittedDate?: string;
  submittedBy?: string
  transactionDate?: string;
  itemIds: number[] = [];
  status: string = 'all';

  statuses: NamedEntity[] = [
    new NamedEntity('all', 'All', { color: 'primary' }),
    new NamedEntity('success', 'Success', { color: 'success' }),
    new NamedEntity('pending', 'Pending', { color: 'warning' }),
    new NamedEntity('canceled', 'Cancelled', { color: 'error' }),
    new NamedEntity('failed', 'Failed', { color: 'error' }),
  ];

  sections: HistoryTypeSection[] = [];

  fontSize: number = 0.875;
  restaurants: NamedEntity[] = [];
  selectedRestaurants: NamedEntity[] = [];
  selectedToReschedule: IScheduledChangeset[] | null = null;

  // Changesets scheduled within the same clock hour are grouped together so they can be
  // rescheduled as one batch instead of one row at a time.
  group: GroupDescriptor[] = [{ field: 'scheduledHour' }];
  
  eod = DateHelper.endOfBusinessDay(new Date());
  rescheduledDate: Date = this.eod;

  cancelAvailabilityConfirmationPopupVisible: boolean = false;

  cancelAvailabilityEnabled: boolean = environment.cancelAvailabilityEnabled;

  constructor(@Inject(CHANGESET_STRATEGIES)
    private strategies: Record<ChangesetType, GridRowType>,
    private changesetService: ChangesetService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tokenService: TokenService,
    private restaurantService: RestaurantService,
    private route: ActivatedRoute,
  ) {
  }

  async ngOnInit() {
    await this.applyQueryParams();
    this.buildSections();
    this.fetchAllSections();

    if (this.hasAvailabilityType) {
      this.getRestaurants();
    }
  }

  async applyQueryParams() {
    const params = this.route.snapshot.queryParamMap;

    let typesParam = params.get('types');

    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    // For AvailabilityOnly users, filter types to only show Availability
    if (this.isAvailabilityOnly) {
      typesParam = 'availability';
    }

    this.types = typesParam?.split(',') ?? []; 

    this.scheduledDate = params.get('scheduledDate') ? params.get('scheduledDate')! : undefined;

    this.tierId = params.get('tierId') ?? undefined;

    this.transmittedDate = params.get('transmittedDate') ? params.get('transmittedDate')! : undefined;
    this.submittedBy = params.get('submittedBy') ?? undefined;

    this.transactionDate = params.get('transactionDate') ? params.get('transactionDate')! : undefined;
    const itemIdsParam = params.get('itemIds');
    if (itemIdsParam) {
      this.itemIds = itemIdsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
    }

    this.status = params.get('syncStatus') ?? 'all';
  }

  get hasAvailabilityType(): boolean {
    return this.types.map(type => type.toLowerCase()).includes('availability');
  }

  buildSections() {
    this.sections = this.types.map((type) => {
      const columns = (this.strategies?.[type.toLowerCase() as ChangesetType]?.getColumns() ?? [])
        .filter((c: ColumnOptions) => c.visibleInQuickView);

      columns
        .filter((c: ColumnOptions) => CUSTOM_FILTER_FIELDS.includes(c.field))
        .forEach((c: ColumnOptions) => (c.filterType = 'custom'));

      const scheduledHourColumn = new ColumnOptions('scheduledHour', 'Scheduled Hour');
      scheduledHourColumn.hidden = true;
      columns.push(scheduledHourColumn);

      return {
        type,
        columns,
        changesets: [],
        state: new GridState(),
        filterCache: new Map<string, string[]>(),
      };
    });
  }

  openRescheduleDialog(group: IScheduledChangeset[]) {
    this.selectedToReschedule = group;
    this.rescheduledDate = this.eod;
  }

  getRestaurants(name?: string) {
    this.loaderService.show();

    const filterComposite = name ? 
    new GridStateCompositeFilterDescriptor().and({
        field: 'name',
        operator: 'contains',
        value: name,
    }) 
    : new GridStateCompositeFilterDescriptor();

    const state = new GridState();
    state.filter = filterComposite;

    this.restaurantService.getRestaurants(state).subscribe({
      next: (restaurants) => {
        this.restaurants = restaurants.data.map(
          (restaurant) =>
            new NamedEntity(restaurant.id, restaurant.name),
        );
        this.loaderService.hide();
      }, 
      error: (err) => {
        this.messageService.error(`Failed to get restaurants: ${err.error}`);
        this.loaderService.hide();
      }
    });
  }

  fetchAllSections() {
    if (this.sections.length === 0) return;

    this.loaderService.show();

    this.sections.forEach((section) => {
      section.distinctParams = this.buildDistinctParams(section);
    });

    forkJoin(
      this.sections.map((section) => this.getSectionChangesets(section)),
    ).subscribe({
      next: (results) => {
        this.sections.forEach((section, index) =>
          this.setSectionChangesets(section, results[index]),
        );
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load changesets');
        this.loaderService.hide();
      },
    });
  }

  private getSectionChangesets(section: HistoryTypeSection) {
    return this.changesetService.getChangesetDetail(
      this.status,
      this.tierId ?? '',
      [section.type],
      this.transactionDate ?? '',
      this.submittedBy ?? '',
      this.scheduledDate ?? '',
      this.transmittedDate ?? '',
      this.itemIds,
      section.state,
    );
  }

  private setSectionChangesets(section: HistoryTypeSection, result: any[]) {
    const relevantChangesets = result
      .filter((item: any) => item.type.toLowerCase() === section.type.toLowerCase())
      .map((item: any) => ({
        ...item,
        scheduledHour: item.scheduledDate ? this.truncateToHour(item.scheduledDate) : null,
      }));

    section.changesets = groupBy(relevantChangesets, this.group) as GroupResult[];
  }

  private buildDistinctParams(section: HistoryTypeSection): Record<string, string | string[]> {
    return {
      status: this.status,
      priceTierId: this.tierId ?? '',
      transactionDate: this.transactionDate ?? '',
      submittedBy: this.submittedBy ?? '',
      scheduledDate: this.scheduledDate ?? '',
      transmittedDate: this.transmittedDate ?? '',
      itemIds: this.itemIds.map(String),
      syncTypes: [section.type],
    };
  }

  onCustomFilterChange(
    section: HistoryTypeSection,
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    section.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  onFilterChange(section: HistoryTypeSection, event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      section.filterCache.clear();
    }

    for (const field of section.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        section.filterCache.delete(field);
      }
    }

    section.state.filter = new GridStateCompositeFilterDescriptor(event);

    this.loaderService.show();
    this.getSectionChangesets(section).subscribe({
      next: (result) => {
        this.setSectionChangesets(section, result);
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load changesets');
        this.loaderService.hide();
      },
    });
  }

  // Buckets a scheduledDate down to the top of its hour so changesets scheduled within the
  // same hour land in the same group (and can be rescheduled together).
  private truncateToHour(date: Date | string): Date {
    const truncated = new Date(date);
    truncated.setMinutes(0, 0, 0);
    return truncated;
  }

  cancelChangeset(dataItem: IScheduledChangeset) {
    this.loaderService.show();
    this.changesetService.cancelChangeset(dataItem.id).subscribe({
      next: () => {
        this.messageService.success('Changeset successfully cancelled');
        this.fetchAllSections();
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to cancel changeset');
        this.loaderService.hide();
      },
    });
  }

  rescheduleChangeset(group: IScheduledChangeset[], scheduledDate: Date) {
    scheduledDate.setSeconds(0, 0); // otherwise the time picker seconds are random

    this.loaderService.show();
    this.changesetService
      .rescheduleChangeset(
        group.map((item: IScheduledChangeset) => item.id),
        scheduledDate,
      )
      .subscribe({
        next: () => {
          this.messageService.success('Changeset successfully rescheduled');
          this.fetchAllSections();
          this.selectedToReschedule = null;
          this.loaderService.hide();
        },
        error: () => {
          this.messageService.error('Failed to reschedule changeset');
          this.selectedToReschedule = null;
          this.loaderService.hide();
        },
      });
  }

  showEditButton(items: IScheduledChangeset[]): boolean {
    return items.every((item) => item.transmitStatus == 'Pending');
  }

  getTimezoneAbbreviation(date: Date): string {
    return DateHelper.getTimezoneAbbreviation(date);
  }

  cancelAvailability() {
    if (this.selectedRestaurants.length === 0) {
      this.messageService.error('Please select a restaurant to cancel availability changesets');
      return;
    }

    this.changesetService.cancelAvailabilityChangesets(
      this.selectedRestaurants.map(r => r.id)
    ).subscribe({
      next: () => {
        this.messageService.success('Availability changesets successfully cancelled');
        this.fetchAllSections();
        this.cancelAvailabilityConfirmationPopupVisible = false;
      },
      error: () => {
        this.messageService.error('Failed to cancel availability changesets');
        this.cancelAvailabilityConfirmationPopupVisible = false;
      },
    });
  }

}
